import React, { useState } from 'react'
import { config, useLatestLotteryResult, useLotteryResults, useClientConfig } from '@workspace/public-utils'
import { motion } from "framer-motion"
import { Search, Trophy, TrendingUp, ChevronLeft, ChevronRight } from "lucide-react"
import LotteryResultDetail from '../components/LotteryResultDetail'
import { Seo } from '../components/Seo'

// LotteryBall component for displaying numbers with Korean lottery colors
const LotteryBall = ({ number, isBonus = false, size = "md" }: {
  number: number;
  isBonus?: boolean;
  size?: "sm" | "md" | "lg"
}) => {
  const getNumberStyle = (num: number) => {
    if (isBonus) return "lottery-number-bonus"
    if (num <= 5) return "lottery-number-1-5"
    if (num <= 10) return "lottery-number-6-10"
    if (num <= 15) return "lottery-number-11-15"
    if (num <= 20) return "lottery-number-16-20"
    if (num <= 25) return "lottery-number-21-25"
    if (num <= 30) return "lottery-number-26-30"
    if (num <= 35) return "lottery-number-31-35"
    if (num <= 40) return "lottery-number-36-40"
    return "lottery-number-41-45"
  }

  const sizeClasses = {
    sm: "w-8 h-8 text-sm",
    md: "w-12 h-12 text-base md:w-16 md:h-16 md:text-xl",
    lg: "w-16 h-16 text-xl md:w-20 md:h-20 md:text-2xl"
  }

  return (
    <motion.div
      className={`${getNumberStyle(number)} ${sizeClasses[size]} rounded-full flex items-center justify-center text-white font-black shadow-lg transform transition-all duration-300 hover:scale-110 cursor-pointer`}
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.5, delay: (number % 6) * 0.1 }}
      whileHover={{ scale: 1.1 }}
    >
      {number}
    </motion.div>
  )
}

// Lottery Result Card Component with Accordion
const LotteryResultCard = ({
  result,
  isLatest = false,
  showDetails = true
}: {
  result: any;
  isLatest?: boolean;
  showDetails?: boolean;
}) => {
  const [expanded, setExpanded] = useState(false)
  
  return (
  <motion.div
    className={`korean-card ${isLatest ? 'korean-card-premium border-happiness-primary' : ''} p-6 mb-4 transition-shadow ${showDetails ? 'cursor-default' : 'cursor-pointer hover:shadow-xl'}`}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    onClick={() => !showDetails && setExpanded(!expanded)}
  >
    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
      <div className="flex items-center space-x-3 mb-4 md:mb-0">
        <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-happiness-primary to-happiness-secondary rounded-full text-white font-black text-lg">
          {result.round_number}
        </div>
        <div>
          <h3 className="text-xl font-black text-gray-900">
            제{result.round_number}회
            {isLatest && <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-happiness-primary text-white">최신</span>}
          </h3>
          <p className="text-sm text-gray-600">{result.draw_date}</p>
        </div>
      </div>

      {isLatest && (
        <div className="flex items-center space-x-2 text-happiness-primary">
          <Trophy className="w-5 h-5" />
          <span className="font-bold">최신 당첨번호</span>
        </div>
      )}
    </div>

    {/* Winning Numbers */}
    <div className="bg-gradient-to-r from-gray-50 to-white rounded-xl p-4 mb-4">
      <p className="text-sm font-bold text-gray-600 mb-3">당첨번호</p>
      <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-4">
        {result.numbers?.map((number: number, index: number) => (
          <LotteryBall key={index} number={number} size="md" />
        ))}
        {result.bonus_number && (
          <>
            <div className="flex items-center mx-2">
              <span className="text-gray-400 font-bold">+</span>
            </div>
            <LotteryBall number={result.bonus_number} isBonus={true} size="md" />
          </>
        )}
      </div>
    </div>

    {/* Prize Information */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-2">
          <Trophy className="w-4 h-4 text-yellow-600" />
          <p className="text-sm font-bold text-yellow-800">1등 당첨자</p>
        </div>
        <p className="text-xl font-black text-yellow-900">
          {result.first_prize_winners ? `${result.first_prize_winners}명` : '-'}
        </p>
      </div>

      <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-2">
          <TrendingUp className="w-4 h-4 text-green-600" />
          <p className="text-sm font-bold text-green-800">1등 상금</p>
        </div>
        <p className="text-xl font-black text-green-900">
          {result.first_prize_amount ? `${(result.first_prize_amount / 100000000).toFixed(1)}억원` : '-'}
        </p>
      </div>
    </div>

    {/* Detailed Prize and Store Information (Accordion) */}
    {showDetails && <LotteryResultDetail roundNumber={result.round_number} />}
  </motion.div>
  )
}

const LotteryResultsPage = () => {
  const clientConfig = useClientConfig()
  const [searchRound, setSearchRound] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [searchedResult, setSearchedResult] = useState<any>(null)
  const [searchLoading, setSearchLoading] = useState(false)
  const [showSearchedResult, setShowSearchedResult] = useState(false)

  const { data: latestResult, isLoading: latestLoading } = useLatestLotteryResult()

  // Server-side pagination - fetch based on current page
  const itemsPerPage = 10
  const skip = (currentPage - 1) * itemsPerPage
  const { data: resultsData, isLoading: resultsLoading } = useLotteryResults(skip, itemsPerPage)

  // Extract results array and total count from API response
  const resultsArray = Array.isArray(resultsData) ? resultsData : (resultsData?.items || [])
  
  // Filter out the latest result from the results array to avoid duplication
  const filteredResults = latestResult 
    ? resultsArray.filter(r => r.round_number !== latestResult.round_number)
    : resultsArray
  
  // Calculate total pages from server response
  const totalCount = resultsData?.total || 0
  const totalPages = Math.ceil(totalCount / itemsPerPage)

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    if (searchRound && parseInt(searchRound) > 0) {
      setSearchLoading(true)
      try {
        // Call the PUBLIC API to get specific round result
        const response = await fetch(`${config.API_BASE_URL}/api/v1/public/lottery/results/${searchRound}`)
        if (response.ok) {
          const result = await response.json()
          // The API returns 'numbers' field directly - no transformation needed
          // Also include prize_details and winning_stores if available
          const transformedResult = {
            round_number: result.round_number,
            draw_date: result.draw_date,
            numbers: result.numbers || result.winning_numbers || [], // Support both field names
            bonus_number: result.bonus_number,
            first_prize_winners: result.first_prize_winners,
            first_prize_amount: result.first_prize_amount,
            total_sales: result.total_sales,
            prize_details: result.prize_details,
            winning_stores: result.winning_stores
          }
          setSearchedResult(transformedResult)
          setShowSearchedResult(true)
        } else {
          alert('해당 회차 결과를 찾을 수 없습니다.')
          setSearchedResult(null)
          setShowSearchedResult(false)
        }
      } catch (error) {
        console.error('Search error:', error)
        alert('검색 중 오류가 발생했습니다.')
        setSearchedResult(null)
        setShowSearchedResult(false)
      } finally {
        setSearchLoading(false)
      }
    } else {
      // Clear search results if search is empty
      setSearchedResult(null)
      setShowSearchedResult(false)
    }
  }

  const clearSearch = () => {
    setSearchRound("")
    setSearchedResult(null)
    setShowSearchedResult(false)
  }

  if (latestLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-happiness-secondary/10 via-white to-happiness-primary/10 pt-16">
        <div className="container mx-auto px-4 py-20">
          <div className="flex justify-center items-center h-64">
            <div className="spinner border-happiness-primary"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-happiness-secondary/10 via-white to-happiness-primary/10 pt-16">
      <Seo
        title="로또 추첨 결과 조회"
        description="최신 로또 6/45 당첨번호와 역대 회차별 추첨 결과를 빠르게 조회하세요."
        canonicalPath="/results"
      />
      <div className="container mx-auto px-4 py-20">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
            <span className="gradient-text-happiness">{clientConfig.brandName}</span> 추첨 결과
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            매주 토요일 오후 8시 45분에 진행되는 로또 6/45 추첨 결과를 확인하세요
          </p>
        </motion.div>

        {/* Search and Filter */}
        <motion.div
          className="bg-white rounded-2xl shadow-xl p-6 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="number"
                  placeholder="회차 번호를 입력하세요"
                  value={searchRound}
                  onChange={(e) => setSearchRound(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-happiness-primary focus:border-transparent"
                />
              </div>
              <button
                type="submit"
                className="btn-primary px-6 py-3"
                disabled={searchLoading}
              >
                {searchLoading ? '검색 중...' : '검색'}
              </button>
              {showSearchedResult && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="btn-secondary px-4 py-3"
                >
                  초기화
                </button>
              )}
            </form>
          </div>
        </motion.div>

        {/* Latest Result or Search Result */}
        {showSearchedResult && searchedResult ? (
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="text-2xl font-black text-gray-900 mb-4">검색 결과: {searchedResult.round_number}회</h2>
            <LotteryResultCard 
              result={searchedResult} 
              isLatest={false} 
              showDetails={true}
            />
          </motion.div>
        ) : latestResult && (
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="text-2xl font-black text-gray-900 mb-4">최신 당첨 결과</h2>
            <LotteryResultCard 
              result={latestResult} 
              isLatest={true} 
              showDetails={true}
            />
          </motion.div>
        )}

        {/* Recent Results - Hide when showing search result */}
        {!showSearchedResult && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h2 className="text-2xl font-black text-gray-900 mb-6">최근 당첨 결과</h2>

            {/* Results List */}
            <div className="space-y-4" data-testid="results-list">
              {resultsLoading ? (
                // Show loading skeleton
                [...Array(itemsPerPage)].map((_, index) => (
                  <div key={index} className="korean-card p-6">
                    <div className="animate-pulse">
                      <div className="flex items-center space-x-4 mb-4">
                        <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                        </div>
                      </div>
                      <div className="flex justify-center space-x-4 mb-4">
                        {[...Array(7)].map((_, i) => (
                          <div key={i} className="w-12 h-12 bg-gray-200 rounded-full"></div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                // Show actual results (server-side pagination - already sliced)
                filteredResults.map((result: any) => (
                  <div key={result.round_number} data-testid="result-row">
                    <LotteryResultCard
                      result={result}
                      isLatest={false}
                      showDetails={true}
                    />
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2 mt-8">
                {/* Previous Button */}
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  aria-label="이전 페이지"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                {/* First Page */}
                {currentPage > 5 && (
                  <>
                    <button
                      onClick={() => setCurrentPage(1)}
                      className="px-4 py-2 rounded-lg font-bold bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                    >
                      1
                    </button>
                    <span className="px-2 text-gray-400">...</span>
                  </>
                )}

                {/* Page Numbers - Show 10 buttons at a time */}
                {(() => {
                  const maxButtons = 10;
                  const halfButtons = Math.floor(maxButtons / 2);
                  
                  let startPage = Math.max(1, currentPage - halfButtons);
                  let endPage = Math.min(totalPages, startPage + maxButtons - 1);
                  
                  // Adjust if we're near the end
                  if (endPage - startPage < maxButtons - 1) {
                    startPage = Math.max(1, endPage - maxButtons + 1);
                  }
                  
                  const pages = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`px-4 py-2 rounded-lg font-bold ${currentPage === i
                          ? 'bg-happiness-primary text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }
                  return pages;
                })()}

                {/* Last Page */}
                {currentPage < totalPages - 5 && (
                  <>
                    <span className="px-2 text-gray-400">...</span>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      className="px-4 py-2 rounded-lg font-bold bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                    >
                      {totalPages}
                    </button>
                  </>
                )}

                {/* Next Button */}
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  aria-label="다음 페이지"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </motion.div>
        )}

        {/* Call to Action */}
        <motion.div
          className="text-center mt-12 p-8 bg-gradient-to-r from-happiness-primary to-happiness-secondary rounded-2xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h3 className="text-2xl font-black text-white mb-4">
            {clientConfig.joinNowCta}
          </h3>
          <p className="text-white/90 mb-6">
            AI 분석으로 생성된 최적의 번호로 당신의 행운을 시작하세요
          </p>
          <button className="btn-gold">
            구독 시작하기
          </button>
        </motion.div>
      </div>
    </div>
  )
}

export default LotteryResultsPage
